import React, { useCallback, useState } from 'react';

import { FormControl, useConfirm } from '@primer/react';

import Button from '../../common/Button/Button';
import CardView from '../../common/CardView/CardView';
import ErrorLabel from '../../common/Labels/ErrorLabel';
import ModalOverlay from '../../common/ModalOverlay/ModalOverlay';

import PipelineCreationFlow from '../PipelineCreationFlow/PipelineCreationFlow';

import { ModelSearchProvider } from '../../models/ModelSearchContext';
import { PipelineCreationProvider } from '../PipelineCreationContext';

import useClientContext from '../../contexts/ClientData/ClientData';
import usePipelineData from '../PipelineDataContext';
import { usePipelineEdgeData } from '../../VersionHistoryTimeline/PublishPipelineContext';

const PipelineDeploymentPanel = () => {
  const [isCreateMenuOpen, setCreateMenuOpen] = useState(false);
  const [deploymentError, setDeploymentError] = useState(false);

  const {
    pipeline,
    deploy,
    undeploy,
    refetchPipelineHistory,
    refetchDeploymentStatus,
    activateUndeployDelay,
    isDeploymentTransitioning,
  } = usePipelineData();

  const { isEdgeEnabled } = useClientContext();
  const {
    isEdgeSelected,
  } = usePipelineEdgeData();

  const confirm = useConfirm();

  const pipelineID = pipeline?.id;
  const pipelineName = pipeline?.pipelineID;

  const deployed = pipeline?.deployment?.deployed ?? false;
  const handleDeployment = useCallback(async (deploymentPrimaryKey) => {
    const confirmOptions = {
      title: 'Deploy Pipeline',
      content: 'Are you sure you want to deploy this pipeline?',
      confirmButtonContent: 'Deploy',
      confirmButtonType: 'primary'
    };

    setDeploymentError(false);
    if (await confirm(confirmOptions)) {
      deploy(deploymentPrimaryKey).then(() => {
        refetchDeploymentStatus();
        setDeploymentError(false)
      }).catch((() => {
        setDeploymentError(true);
      }));
    }
  }, [confirm, deploy, refetchDeploymentStatus]);

  const handleUndeployment = useCallback(async (deploymentPrimaryKey) => {
    const confirmOptions = {
      title: 'Undeploy Pipeline',
      content: 'Are you sure you want to undeploy this pipeline?',
      confirmButtonContent: 'Undeploy',
      confirmButtonType: 'danger'
    };

    setDeploymentError(false);
    if (await confirm(confirmOptions)) {
      undeploy(deploymentPrimaryKey).then(() => {
        activateUndeployDelay();
        refetchDeploymentStatus();
        setDeploymentError(false);
      }).catch((() => {
        setDeploymentError(true);
      }));
    }
  }, [activateUndeployDelay, confirm, refetchDeploymentStatus, undeploy]);

  const onDeployClick = useCallback(() => {
    if (deployed) {
      handleUndeployment({ pipeline_id: pipelineID });
    } else {
      handleDeployment(pipelineName)
    }
  }, [deployed, handleUndeployment, pipelineID, handleDeployment, pipelineName])

  const errorMessage = `Something went wrong while trying to ${deployed ? 'undeploy' : 'deploy'} your pipeline.`;

  const disableDeploymentButton = isDeploymentTransitioning || (isEdgeEnabled && isEdgeSelected);
  return (
    <>
      <CardView className="mb-2">
        <FormControl>
          <FormControl.Label visuallyHidden>{deployed ? 'Undeploy' : 'Deploy'}</FormControl.Label>
          <Button
            className="w-full"
            size="large"
            variant={deployed ? 'danger' : 'primary'}
            onClick={onDeployClick}
            disabled={disableDeploymentButton}
          >
            {deployed ? 'Undeploy' : 'Deploy'}
          </Button>
          {
            isEdgeEnabled && deployed && (
              <div className="w-full flex items-center justify-center">
                <ErrorLabel>This will only undeploy pipeline version in the Ops Center</ErrorLabel>
              </div>
            )
          }
          {
            deploymentError && (
              <FormControl.Validation variant="error">
                {errorMessage}
              </FormControl.Validation>
            )
          }
        </FormControl>
      </CardView>
      {
        isCreateMenuOpen ? (
          <ModalOverlay
            animationKey="create-pipeline-overlay"
            active={isCreateMenuOpen}
            onModalToggle={setCreateMenuOpen}
          >
            <PipelineCreationProvider pipelineRefetch={refetchPipelineHistory}>
              <ModelSearchProvider>
                <PipelineCreationFlow onClose={() => setCreateMenuOpen(false)} />
              </ModelSearchProvider>
            </PipelineCreationProvider>
          </ModalOverlay>
        ) : null
      }
    </>
  )
}

export default PipelineDeploymentPanel;
